import { useState, useEffect } from 'react';
import { sectorsApi, establishmentsApi } from '@/lib/api';
import { Building2, Plus, Edit2, Trash2, X, Check, AlertCircle, MapPin, Users, Layers } from 'lucide-react';

export default function SectorsPage() {
  const [sectors, setSectors] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', icon: 'building' });
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const [s, e] = await Promise.all([sectorsApi.list(), establishmentsApi.list()]);
      setSectors(s.data);
      setEstablishments(e.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setError('');
    try {
      const slug = form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const payload = { ...form, slug };
      if (editing) {
        await sectorsApi.update(editing, payload);
      } else {
        await sectorsApi.create(payload);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', slug: '', description: '', icon: 'building' });
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (s) => {
    setForm({ name: s.name, slug: s.slug, description: s.description || '', icon: s.icon || 'building' });
    setEditing(s.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce secteur ?')) return;
    try {
      await sectorsApi.delete(id);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossible de supprimer');
    }
  };

  const getEstCount = (sectorId) => establishments.filter(e => e.sector_id === sectorId).length;

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-[#1B2A4A]/20 border-t-[#1B2A4A] rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="sectors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Secteurs & segmentation</h1>
          <p className="text-gray-400 mt-1 text-sm">G&eacute;rez les secteurs d'activit&eacute; et les zones g&eacute;ographiques</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', slug: '', description: '', icon: 'building' }); }}
          className="bg-[#B8860B] text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#9A7009] transition-all shadow-md flex items-center gap-2"
          data-testid="add-sector-btn"
        >
          <Plus size={16} /> Nouveau secteur
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="sector-error">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6" data-testid="sector-form">
          <h2 className="font-bold text-[#1B2A4A] font-manrope mb-4">{editing ? 'Modifier le secteur' : 'Nouveau secteur'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Nom du secteur</label>
              <input
                data-testid="sector-name-input"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="ex: Restaurants italiens"
                className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Slug (identifiant)</label>
              <input
                data-testid="sector-slug-input"
                value={form.slug}
                onChange={e => setForm({ ...form, slug: e.target.value })}
                placeholder="restaurants-italiens (auto)"
                className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Description</label>
              <input
                data-testid="sector-desc-input"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Description courte du secteur"
                className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} className="bg-[#1B2A4A] text-white px-5 py-2 rounded-lg font-semibold text-sm hover:bg-[#152039] flex items-center gap-2" data-testid="sector-save-btn">
              <Check size={16} /> {editing ? 'Mettre à jour' : 'Créer'}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="bg-gray-100 text-gray-600 px-5 py-2 rounded-lg font-semibold text-sm hover:bg-gray-200">
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Sectors grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sectors.map(s => {
          const count = getEstCount(s.id);
          const zones = [...new Set(establishments.filter(e => e.sector_id === s.id).map(e => e.zone).filter(Boolean))];
          return (
            <div key={s.id} className="bg-white rounded-xl border border-gray-100 shadow-[0_2px_8px_rgba(27,42,74,0.04)] p-5 hover:shadow-md transition-all" data-testid={`sector-card-${s.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-[#1B2A4A] font-manrope">{s.name}</h3>
                  {s.description && <p className="text-xs text-gray-400 mt-1">{s.description}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleEdit(s)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1B2A4A] transition-all" data-testid={`sector-edit-${s.id}`}>
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(s.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-all" data-testid={`sector-delete-${s.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Building2 size={12} /> {count} étab.</span>
                <span className="flex items-center gap-1"><Layers size={12} /> {count > 0 ? `${count - establishments.filter(e => e.sector_id === s.id && e.is_demo).length} client(s)` : '0 client'}</span>
              </div>
              {zones.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {zones.map(z => (
                    <span key={z} className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-[10px] text-gray-500 flex items-center gap-1">
                      <MapPin size={8} /> {z}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info box */}
      <div className="bg-[#1B2A4A]/[0.03] rounded-xl p-5 border border-[#1B2A4A]/5">
        <h3 className="text-sm font-bold text-[#1B2A4A] mb-2 flex items-center gap-2"><Layers size={14} /> Comment fonctionne la segmentation</h3>
        <p className="text-xs text-gray-600 leading-relaxed">
          Chaque établissement est automatiquement comparé aux autres établissements du même <strong>secteur</strong>. Si suffisamment de données sont disponibles, la comparaison est affinée par <strong>zone géographique</strong> et par <strong>taille</strong>. Vous n'avez rien à configurer manuellement : ajoutez un secteur, créez vos établissements avec leur zone, et le moteur de benchmark fait le reste.
        </p>
      </div>
    </div>
  );
}
